import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { HardDrive, Cloud, Loader2, Save, Upload } from 'lucide-react';
import { MissingFilesAudit, Stat } from './MissingFilesAudit';
import { DriveBackupCard } from '../DriveBackupCard';

const MODES = [
  { id: 'local',    label: 'Local Disk',       desc: 'Stored on the server (wipes on redeploy)',         icon: HardDrive },
  { id: 'r2',       label: 'Cloudflare R2',    desc: 'S3-compatible. Persistent. Your account.',         icon: Cloud },
  { id: 'emergent', label: 'Emergent Storage', desc: 'Managed by Emergent. Uses your Universal Key.',    icon: Cloud },
];

/** StorageTab — switch backend (Local / R2 / Emergent) + run one-shot migration
 *  + audit missing files + Google-Drive secondary backup. The biggest tab
 *  in this page (it owns all file-related infra config).
 */
export function StorageTab({
  api, storage, setStorage, saveStorage, testStorage,
  migration, startMigration, saving,
}) {
  return (
    <>
      <Card className="bg-[#041A3B] border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <HardDrive className="w-5 h-5" />
            File Storage Backend
          </CardTitle>
          <CardDescription className="text-[#A0B4CC]">
            Choose where uploaded images and files are stored. Active mode:{' '}
            <b className="text-[#00E5FF]" data-testid="storage-active-mode">{(storage.active_mode || 'local').toUpperCase()}</b>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Mode picker */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {MODES.map((m) => {
              const Icon = m.icon;
              const active = storage.mode === m.id;
              return (
                <button
                  key={m.id} type="button"
                  onClick={() => setStorage(prev => ({ ...prev, mode: m.id }))}
                  data-testid={`storage-mode-${m.id}`}
                  className={`p-4 rounded-lg border text-left transition-colors ${
                    active ? 'border-[#00E5FF] bg-[#00E5FF]/10' : 'border-white/10 bg-[#000B18] hover:border-white/30'
                  }`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${active ? 'text-[#00E5FF]' : 'text-[#A0B4CC]'}`} />
                  <p className="text-white font-medium text-sm">{m.label}</p>
                  <p className="text-[#5C7599] text-xs mt-1">{m.desc}</p>
                </button>
              );
            })}
          </div>

          {/* Enabled switch */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
            <div>
              <Label className="text-white">Storage backend enabled</Label>
              <p className="text-[#5C7599] text-xs">When off, falls back to Local Disk regardless of selected mode.</p>
            </div>
            <Switch
              checked={storage.is_enabled}
              onCheckedChange={(v) => setStorage(prev => ({ ...prev, is_enabled: v }))}
              data-testid="storage-enabled-switch"
            />
          </div>

          {storage.mode === 'r2' && (
            <div className="space-y-4 p-4 rounded-lg bg-[#000B18]/60 border border-white/10">
              <p className="text-[#A0B4CC] text-sm">Get these from Cloudflare → R2 → &quot;Manage R2 API Tokens&quot;.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">R2 Account ID</Label>
                  <Input value={storage.r2_account_id} onChange={(e) => setStorage(prev => ({ ...prev, r2_account_id: e.target.value }))}
                    placeholder="e.g., 1a2b3c4d…" className="bg-[#041A3B] border-white/20 text-white font-mono" data-testid="r2-account-id" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">R2 Bucket Name</Label>
                  <Input value={storage.r2_bucket} onChange={(e) => setStorage(prev => ({ ...prev, r2_bucket: e.target.value }))}
                    placeholder="aga-uploads" className="bg-[#041A3B] border-white/20 text-white font-mono" data-testid="r2-bucket" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">R2 Access Key ID</Label>
                  <Input value={storage.r2_access_key_id} onChange={(e) => setStorage(prev => ({ ...prev, r2_access_key_id: e.target.value }))}
                    placeholder="40-char key" className="bg-[#041A3B] border-white/20 text-white font-mono" data-testid="r2-access-key" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">
                    R2 Secret Access Key {storage.r2_secret_access_key_set && <span className="text-[#00E676] text-xs ml-2">(saved)</span>}
                  </Label>
                  <Input type="password" value={storage.r2_secret_access_key}
                    onChange={(e) => setStorage(prev => ({ ...prev, r2_secret_access_key: e.target.value }))}
                    placeholder={storage.r2_secret_access_key_set ? '••••••••• (leave blank to keep)' : 'Paste secret'}
                    className="bg-[#041A3B] border-white/20 text-white font-mono" data-testid="r2-secret-key" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-white">R2 Public URL (optional)</Label>
                <Input value={storage.r2_public_url} onChange={(e) => setStorage(prev => ({ ...prev, r2_public_url: e.target.value }))}
                  placeholder="https://pub-xxxx.r2.dev or https://uploads.yourdomain.com"
                  className="bg-[#041A3B] border-white/20 text-white" data-testid="r2-public-url" />
                <p className="text-[#5C7599] text-xs">
                  If blank, files are proxied through <code>/api/files/r2/...</code>. Set a public URL or custom domain for faster delivery.
                </p>
              </div>
              <Button onClick={testStorage} disabled={saving} variant="outline" className="border-white/20 text-white" data-testid="test-r2-btn">
                Test R2 Connection
              </Button>
            </div>
          )}

          {storage.mode === 'emergent' && (
            <div className="space-y-4 p-4 rounded-lg bg-[#000B18]/60 border border-white/10">
              <p className="text-[#A0B4CC] text-sm">
                Managed by Emergent — uses your Universal Key. Persistent across deployments. Get your key from Profile → Universal Key.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">
                    Emergent Universal Key {storage.emergent_key_set && <span className="text-[#00E676] text-xs ml-2">(saved)</span>}
                  </Label>
                  <Input type="password" value={storage.emergent_key}
                    onChange={(e) => setStorage(prev => ({ ...prev, emergent_key: e.target.value }))}
                    placeholder={storage.emergent_key_set ? '••••••••• (leave blank to keep)' : 'sk-emergent-…'}
                    className="bg-[#041A3B] border-white/20 text-white font-mono" data-testid="emergent-key" />
                </div>
                <div className="space-y-2">
                  <Label className="text-white">App Namespace</Label>
                  <Input value={storage.emergent_app_name}
                    onChange={(e) => setStorage(prev => ({ ...prev, emergent_app_name: e.target.value }))}
                    placeholder="all-global-access"
                    className="bg-[#041A3B] border-white/20 text-white font-mono" data-testid="emergent-app-name" />
                </div>
              </div>
            </div>
          )}

          {storage.mode === 'local' && (
            <div className="p-4 rounded-lg bg-[#FFB300]/10 border border-[#FFB300]/30">
              <p className="text-[#FFB300] text-sm">
                ⚠ Local Disk mode is fine for development but uploaded images are <b>lost on each production deploy</b>.
                Switch to Cloudflare R2 or Emergent Storage for production.
              </p>
            </div>
          )}

          <Button onClick={saveStorage} disabled={saving} className="btn-primary w-full" data-testid="save-storage-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Storage Configuration
          </Button>

          <div
            className={`p-3 rounded-lg text-sm border ${
              (storage.active_mode || 'local').toLowerCase() === (storage.mode || 'local').toLowerCase()
                ? 'bg-[#00E676]/10 border-[#00E676]/30 text-[#A0B4CC]'
                : 'bg-[#FF1744]/10 border-[#FF1744]/30 text-[#FFB4B4]'
            }`}
            data-testid="storage-active-mode"
          >
            <p>
              Active mode: <b className="text-white">{(storage.active_mode || 'local').toUpperCase()}</b>
              {(storage.active_mode || 'local').toLowerCase() !== (storage.mode || 'local').toLowerCase() && (
                <span className="ml-2 text-[#FF1744]">— fell back from {(storage.mode || '').toUpperCase()}</span>
              )}
            </p>
            {storage.active_mode_reason && (
              <p className="text-xs mt-1 opacity-80" data-testid="storage-active-mode-reason">{storage.active_mode_reason}</p>
            )}
          </div>

          <Button
            onClick={testStorage} variant="outline"
            className="w-full border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:text-[#00E5FF]"
            data-testid="test-storage-btn"
          >
            Test Connection
          </Button>
        </CardContent>
      </Card>

      {/* Migration Card */}
      <Card className="bg-[#041A3B] border-white/10 mt-6">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Upload className="w-5 h-5" />
            One-shot Migration
          </CardTitle>
          <CardDescription className="text-[#A0B4CC]">
            Move existing uploaded files from Local Disk → {(storage.active_mode || 'local').toUpperCase()}. References across the app are rewritten automatically. Re-runnable &amp; idempotent.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {storage.active_mode === 'local' ? (
            <div className="p-4 rounded-lg bg-[#5C7599]/15 border border-[#5C7599]/30 text-[#A0B4CC] text-sm">
              Active mode is <b>LOCAL</b>. Switch to R2 or Emergent above, save, and then run the migration.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="migration-totals">
                <Stat label="DB scanned"   value={migration.totals?.scanned_db || 0} color="#A0B4CC" />
                <Stat label="FS scanned"   value={migration.totals?.scanned_fs || 0} color="#A0B4CC" />
                <Stat label="Uploaded"     value={migration.totals?.uploaded || 0}   color="#00E676" />
                <Stat label="Skipped"      value={migration.totals?.skipped || 0}    color="#FFB300" />
                <Stat label="Rewrote refs" value={migration.totals?.rewrote || 0}    color="#00E5FF" />
              </div>
              <div className="flex items-center justify-between p-3 rounded bg-[#000B18]">
                <div>
                  <p className="text-white text-sm font-medium">
                    Status: <span className={
                      migration.state === 'running' ? 'text-[#FFB300]' :
                      migration.state === 'complete' ? 'text-[#00E676]' :
                      migration.state === 'complete_with_errors' ? 'text-[#FF1744]' : 'text-[#A0B4CC]'
                    } data-testid="migration-state">{migration.state}</span>
                  </p>
                  {migration.totals?.errors > 0 && (
                    <p className="text-[#FF1744] text-xs mt-1">{migration.totals.errors} errors — see log below.</p>
                  )}
                </div>
                <Button onClick={startMigration} disabled={migration.state === 'running' || saving} className="btn-primary" data-testid="run-migration-btn">
                  {migration.state === 'running' ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Running…</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Run Migration</>
                  )}
                </Button>
              </div>
              {migration.errors && migration.errors.length > 0 && (
                <details className="rounded bg-[#000B18] border border-white/10">
                  <summary className="cursor-pointer p-3 text-[#FF1744] text-sm">
                    View {migration.errors.length} error{migration.errors.length === 1 ? '' : 's'}
                  </summary>
                  <ul className="p-3 pt-0 text-[#A0B4CC] text-xs space-y-1 max-h-48 overflow-y-auto">
                    {migration.errors.map((err, i) => (
                      <li key={`mig-err-${i}-${String(err).slice(0, 24)}`} className="font-mono">{err}</li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Missing-files audit */}
      <MissingFilesAudit api={api} />

      {/* Google Drive backup */}
      <DriveBackupCard api={api} />
    </>
  );
}

export default StorageTab;
